import { useState, useMemo, useEffect } from "react";
import AdminLayout from "@/components/layout-admin";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Trash2,
  Eye,
  Loader2,
  Send,
  CheckCircle,
  DollarSign,
  ClipboardList,
  Download,
  Building2,
  Link2,
  CreditCard,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { generateContractorInvoicePdf, generateInvoicePdf } from "@/lib/invoice-pdf";

function formatDate(d: string | Date) {
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

function formatMoney(amount: string | number | null | undefined): string {
  const num = Number(amount || 0);
  return `$${num.toFixed(2)}`;
}

function statusVariant(status: string): "secondary" | "default" | "outline" | "destructive" {
  switch (status) {
    case "draft": return "secondary";
    case "pending": return "outline";
    case "approved": return "default";
    case "paid": return "secondary";
    default: return "secondary";
  }
}

function statusClassName(status: string): string {
  if (status === "paid") return "bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-900 dark:text-green-300";
  return "";
}

function statusLabel(status: string): string {
  switch (status) {
    case "draft": return "Draft";
    case "pending": return "Pending";
    case "approved": return "Approved";
    case "paid": return "Paid";
    default: return status;
  }
}

const emptyProfileForm = {
  companyName: "",
  taxId: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
  billingEmail: "",
  billingPhone: "",
};

export default function AdminContractorInvoices() {
  const { toast } = useToast();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [selectedReceivedInvoice, setSelectedReceivedInvoice] = useState<any>(null);
  const [deleteInvoiceId, setDeleteInvoiceId] = useState<number | null>(null);

  const [contractorUserId, setContractorUserId] = useState("");
  const [periodStart, setPeriodStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0];
  });
  const [periodEnd, setPeriodEnd] = useState(() => new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");

  const [profileCleanerId, setProfileCleanerId] = useState<number | null>(null);
  const [profileForm, setProfileForm] = useState({ ...emptyProfileForm });

  const { data: users } = useQuery<any[]>({
    queryKey: ["/api/admin/users"],
  });

  const contractors = useMemo(
    () => (users || []).filter((u: any) => u.role === "cleaner" || u.role === "maintenance" || ((u.additionalRoles as string[] | undefined) ?? []).some(r => r === "cleaner" || r === "maintenance")),
    [users]
  );

  const { data: invoices, isLoading: invoicesLoading } = useQuery<any[]>({
    queryKey: ["/api/admin/contractor-invoices"],
  });

  const { data: invoiceDetail, isLoading: detailLoading } = useQuery<any>({
    queryKey: ["/api/admin/contractor-invoices", selectedInvoice?.id],
    enabled: !!selectedInvoice?.id,
  });

  const { data: billingProfiles, isLoading: profilesLoading } = useQuery<any[]>({
    queryKey: ["/api/admin/cleaner-billing-profiles"],
  });

  const { data: receivedInvoices, isLoading: receivedLoading } = useQuery<any[]>({
    queryKey: ["/api/admin/owner-invoices/received"],
  });

  const { data: selectedProfile } = useQuery<any>({
    queryKey: ["/api/admin/cleaner-billing-profiles", profileCleanerId],
    enabled: !!profileCleanerId,
  });

  useEffect(() => {
    if (selectedProfile) {
      setProfileForm({
        companyName: selectedProfile.companyName || "",
        taxId: selectedProfile.taxId || "",
        addressLine1: selectedProfile.addressLine1 || "",
        addressLine2: selectedProfile.addressLine2 || "",
        city: selectedProfile.city || "",
        state: selectedProfile.state || "",
        postalCode: selectedProfile.postalCode || "",
        country: selectedProfile.country || "",
        billingEmail: selectedProfile.billingEmail || "",
        billingPhone: selectedProfile.billingPhone || "",
      });
    } else if (profileCleanerId) {
      setProfileForm({ ...emptyProfileForm });
    }
  }, [selectedProfile, profileCleanerId]);

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/admin/contractor-invoices", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/contractor-invoices"] });
      setShowCreateDialog(false);
      setContractorUserId("");
      setNotes("");
      toast({ title: "Invoice created", description: "Contractor invoice has been generated from financial records." });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message || "Failed to create invoice", variant: "destructive" });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const res = await apiRequest("PUT", `/api/admin/contractor-invoices/${id}`, { status });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/contractor-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/contractor-invoices", data.id] });
      setSelectedInvoice(data);
      toast({ title: data.status === "paid" ? "Invoice marked as paid" : "Invoice updated" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message || "Failed to update invoice", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/admin/contractor-invoices/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/contractor-invoices"] });
      setDeleteInvoiceId(null);
      setSelectedInvoice(null);
      toast({ title: "Invoice deleted" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message || "Failed to delete invoice", variant: "destructive" });
    },
  });

  const saveProfileMutation = useMutation({
    mutationFn: async ({ cleanerUserId, data }: { cleanerUserId: number; data: typeof profileForm }) => {
      const res = await apiRequest("PUT", `/api/admin/cleaner-billing-profiles/${cleanerUserId}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/cleaner-billing-profiles"] });
      if (profileCleanerId) {
        queryClient.invalidateQueries({ queryKey: ["/api/admin/cleaner-billing-profiles", profileCleanerId] });
      }
      setProfileCleanerId(null);
      toast({ title: "Profile saved", description: "Billing profile has been updated." });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message || "Failed to save profile", variant: "destructive" });
    },
  });

  function openProfileEditor(cleanerId: number) {
    const existing = (billingProfiles || []).find((p: any) => p.cleanerUserId === cleanerId);
    if (existing) {
      setProfileForm({
        companyName: existing.companyName || "",
        taxId: existing.taxId || "",
        addressLine1: existing.addressLine1 || "",
        addressLine2: existing.addressLine2 || "",
        city: existing.city || "",
        state: existing.state || "",
        postalCode: existing.postalCode || "",
        country: existing.country || "",
        billingEmail: existing.billingEmail || "",
        billingPhone: existing.billingPhone || "",
      });
    } else {
      setProfileForm({ ...emptyProfileForm });
    }
    setProfileCleanerId(cleanerId);
  }

  function handleCreate() {
    if (!contractorUserId) {
      toast({ title: "Error", description: "Please select a contractor", variant: "destructive" });
      return;
    }
    if (!periodStart || !periodEnd) {
      toast({ title: "Error", description: "Please set the period start and end dates", variant: "destructive" });
      return;
    }
    createMutation.mutate({
      contractorUserId: parseInt(contractorUserId),
      periodStart,
      periodEnd,
      notes: notes || undefined,
    });
  }

  function getContractorName(userId: number | string) {
    const user = (users || []).find((u: any) => u.id === Number(userId));
    return user ? (user.fullName || user.username) : `User #${userId}`;
  }

  const detail = invoiceDetail || selectedInvoice;
  const profileContractor = profileCleanerId ? contractors.find((c: any) => c.id === profileCleanerId) : null;

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold" data-testid="text-page-title">Contractor Invoices</h1>
            <p className="text-sm text-muted-foreground">Generate invoices from financial records for contractors</p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="button-create-invoice">
            <Plus className="h-4 w-4 mr-2" />
            Generate Invoice
          </Button>
        </div>

        <Tabs defaultValue="invoices">
          <TabsList>
            <TabsTrigger value="invoices" data-testid="tab-invoices">From Cleaners</TabsTrigger>
            <TabsTrigger value="from-orgs" data-testid="tab-from-orgs">
              From Organizations
              {(receivedInvoices || []).filter((i: any) => i.status === 'draft' || i.status === 'approved').length > 0 && (
                <span className="ml-1.5 rounded-full bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5">
                  {(receivedInvoices || []).filter((i: any) => i.status === 'draft' || i.status === 'approved').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="billing-profiles" data-testid="tab-billing-profiles">Billing Profiles</TabsTrigger>
          </TabsList>

          <TabsContent value="invoices" className="space-y-4 mt-4">
            {invoicesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (invoices || []).length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No contractor invoices yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Click "Generate Invoice" to create one from financial records</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {(invoices || []).map((inv: any) => (
                  <Card
                    key={inv.id}
                    className="cursor-pointer hover-elevate"
                    onClick={() => setSelectedInvoice(inv)}
                    data-testid={`card-invoice-${inv.id}`}
                  >
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-mono text-sm font-medium" data-testid={`text-invoice-number-${inv.id}`}>
                            {inv.invoiceNumber || `INV-${inv.id}`}
                          </p>
                          <p className="text-sm text-muted-foreground truncate" data-testid={`text-contractor-name-${inv.id}`}>
                            {getContractorName(inv.contractorUserId)}
                          </p>
                        </div>
                        <Badge variant={statusVariant(inv.status)} className={statusClassName(inv.status)} data-testid={`badge-status-${inv.id}`}>
                          {statusLabel(inv.status)}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {inv.weekStartDate && inv.weekEndDate
                          ? `${formatDate(inv.weekStartDate)} - ${formatDate(inv.weekEndDate)}`
                          : "No dates"}
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-lg font-semibold" data-testid={`text-total-${inv.id}`}>
                          {formatMoney(inv.totalAmount)}
                        </span>
                        <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); setSelectedInvoice(inv); }} data-testid={`button-view-invoice-${inv.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="from-orgs" className="space-y-4 mt-4">
            {receivedLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (receivedInvoices || []).length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No invoices from service organizations</p>
                  <p className="text-sm text-muted-foreground mt-1">Invoices sent by service orgs you work with will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>From</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(receivedInvoices || []).map((inv: any) => (
                      <TableRow key={inv.id}>
                        <TableCell className="font-mono text-sm">{inv.invoiceNumber || `INV-${inv.id}`}</TableCell>
                        <TableCell className="font-medium">{inv.fromOrgName || `Org #${inv.organizationId}`}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {inv.periodStart && inv.periodEnd
                            ? `${formatDate(inv.periodStart)} — ${formatDate(inv.periodEnd)}`
                            : "—"}
                        </TableCell>
                        <TableCell className="font-semibold">{formatMoney(inv.total)}</TableCell>
                        <TableCell>
                          <Badge variant={statusVariant(inv.status)} className={statusClassName(inv.status)}>
                            {statusLabel(inv.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => setSelectedReceivedInvoice(inv)}>
                            <Eye className="h-3.5 w-3.5 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="billing-profiles" className="space-y-4 mt-4">
            {profilesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Contractor Billing Profiles</CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Contractor</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Tax ID</TableHead>
                        <TableHead>Billing Email</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contractors.map((contractor: any) => {
                        const profile = (billingProfiles || []).find((p: any) => p.cleanerUserId === contractor.id);
                        return (
                          <TableRow key={contractor.id} data-testid={`row-contractor-${contractor.id}`}>
                            <TableCell className="font-medium">{contractor.fullName || contractor.username}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs capitalize">
                                {contractor.role === "cleaner" ? "Cleaning" : "Maintenance"}
                              </Badge>
                            </TableCell>
                            <TableCell>{profile?.companyName || <span className="text-muted-foreground">Not set</span>}</TableCell>
                            <TableCell>{profile?.taxId || "-"}</TableCell>
                            <TableCell>{profile?.billingEmail || "-"}</TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openProfileEditor(contractor.id)}
                                data-testid={`button-edit-profile-${contractor.id}`}
                              >
                                <Building2 className="h-4 w-4 mr-1" />
                                {profile ? "Edit" : "Set Up"}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {contractors.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                            No contractors found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate Contractor Invoice</DialogTitle>
            <DialogDescription>
              Select a contractor and date range. The system will automatically pull all financial records for that period and generate the invoice.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Contractor</Label>
              <SearchableSelect
                inDialog
                value={contractorUserId}
                onValueChange={setContractorUserId}
                options={(contractors as any[]).map((c) => ({
                  value: String(c.id),
                  label: `${c.fullName || c.username} (${c.role === "cleaner" ? "Cleaning" : "Maintenance"})${c.isActive === false ? " — inactive" : ""}`,
                }))}
                placeholder="Select contractor"
                data-testid="select-contractor"
                className="w-full"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Period Start</Label>
                <Input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} data-testid="input-period-start" />
              </div>
              <div>
                <Label>Period End</Label>
                <Input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} data-testid="input-period-end" />
              </div>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Additional notes..." data-testid="input-notes" />
            </div>
            <Button className="w-full" onClick={handleCreate} disabled={createMutation.isPending} data-testid="button-submit-create">
              {createMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : "Generate Invoice"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedInvoice} onOpenChange={(open) => { if (!open) setSelectedInvoice(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invoice Details</DialogTitle>
            <DialogDescription>
              {detail?.invoiceNumber || `Invoice #${detail?.id}`}
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : detail ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Contractor</p>
                  <p className="font-medium" data-testid="text-detail-contractor">
                    {detail.contractor?.fullName || detail.contractor?.username || getContractorName(detail.contractorUserId)}
                  </p>
                  {detail.contractor?.role && (
                    <p className="text-xs text-muted-foreground capitalize">{detail.contractor.role === "cleaner" ? "Cleaning" : "Maintenance"}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant={statusVariant(detail.status)} className={statusClassName(detail.status)} data-testid="badge-detail-status">
                    {statusLabel(detail.status)}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Period</p>
                  <p className="font-medium" data-testid="text-detail-period">
                    {detail.weekStartDate && detail.weekEndDate
                      ? `${formatDate(detail.weekStartDate)} - ${formatDate(detail.weekEndDate)}`
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Amount</p>
                  <p className="text-lg font-bold" data-testid="text-detail-total">
                    {formatMoney(detail.totalAmount)}
                  </p>
                </div>
              </div>

              {detail.notes && (
                <div>
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="text-sm" data-testid="text-detail-notes">{detail.notes}</p>
                </div>
              )}

              <div>
                <p className="text-sm font-medium mb-2">Line Items</p>
                <Card>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Description</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(detail.items || []).map((item: any, idx: number) => (
                          <TableRow key={idx} data-testid={`row-item-${idx}`}>
                            <TableCell>{item.description}</TableCell>
                            <TableCell className="text-muted-foreground capitalize">{item.jobType || "-"}</TableCell>
                            <TableCell className="text-right font-medium">{formatMoney(item.amount)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-muted/50">
                          <TableCell colSpan={2} className="font-semibold">Total</TableCell>
                          <TableCell className="text-right font-bold">{formatMoney(detail.totalAmount)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>

              <div className="flex items-center gap-2 flex-wrap pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    try {
                      generateContractorInvoicePdf({
                        ...detail,
                        contractorName: detail.contractor?.fullName || detail.contractor?.username || getContractorName(detail.contractorUserId),
                      });
                    } catch {
                      toast({ title: "Error", description: "Failed to generate PDF.", variant: "destructive" });
                    }
                  }}
                  data-testid="button-download-pdf"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
                {detail.status === "draft" && (
                  <>
                    <Button
                      onClick={() => updateStatusMutation.mutate({ id: detail.id, status: "pending" })}
                      disabled={updateStatusMutation.isPending}
                      data-testid="button-submit-invoice"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Submit
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => setDeleteInvoiceId(detail.id)}
                      data-testid="button-delete-invoice"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </>
                )}
                {detail.status === "pending" && (
                  <>
                    <Button
                      onClick={() => updateStatusMutation.mutate({ id: detail.id, status: "approved" })}
                      disabled={updateStatusMutation.isPending}
                      data-testid="button-approve-invoice"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      variant="default"
                      onClick={() => updateStatusMutation.mutate({ id: detail.id, status: "paid" })}
                      disabled={updateStatusMutation.isPending}
                      data-testid="button-mark-paid"
                    >
                      <DollarSign className="h-4 w-4 mr-2" />
                      Mark Paid
                    </Button>
                  </>
                )}
                {detail.status === "approved" && (
                  <Button
                    variant="default"
                    onClick={() => updateStatusMutation.mutate({ id: detail.id, status: "paid" })}
                    disabled={updateStatusMutation.isPending}
                    data-testid="button-mark-paid"
                  >
                    <DollarSign className="h-4 w-4 mr-2" />
                    Mark Paid
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={profileCleanerId !== null} onOpenChange={(open) => { if (!open) setProfileCleanerId(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Contractor Billing Profile</DialogTitle>
            <DialogDescription>
              {profileContractor ? (profileContractor.fullName || profileContractor.username) : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Company / Legal Name <span className="text-destructive">*</span></Label>
              <Input
                value={profileForm.companyName}
                onChange={e => setProfileForm({ ...profileForm, companyName: e.target.value })}
                placeholder="e.g. Maria Rodriguez Cleaning LLC"
                data-testid="input-company-name"
              />
            </div>
            <div>
              <Label>Tax ID / EIN</Label>
              <Input
                value={profileForm.taxId}
                onChange={e => setProfileForm({ ...profileForm, taxId: e.target.value })}
                placeholder="XX-XXXXXXX"
                data-testid="input-tax-id"
              />
            </div>
            <div>
              <Label>Address Line 1</Label>
              <Input
                value={profileForm.addressLine1}
                onChange={e => setProfileForm({ ...profileForm, addressLine1: e.target.value })}
                placeholder="Street address"
                data-testid="input-address-line1"
              />
            </div>
            <div>
              <Label>Address Line 2</Label>
              <Input
                value={profileForm.addressLine2}
                onChange={e => setProfileForm({ ...profileForm, addressLine2: e.target.value })}
                placeholder="Suite, apt, etc."
                data-testid="input-address-line2"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={profileForm.city}
                  onChange={e => setProfileForm({ ...profileForm, city: e.target.value })}
                  placeholder="City"
                  data-testid="input-city"
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={profileForm.state}
                  onChange={e => setProfileForm({ ...profileForm, state: e.target.value })}
                  placeholder="State"
                  data-testid="input-state"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>ZIP / Postal Code</Label>
                <Input
                  value={profileForm.postalCode}
                  onChange={e => setProfileForm({ ...profileForm, postalCode: e.target.value })}
                  placeholder="ZIP"
                  data-testid="input-postal-code"
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={profileForm.country}
                  onChange={e => setProfileForm({ ...profileForm, country: e.target.value })}
                  placeholder="US"
                  data-testid="input-country"
                />
              </div>
            </div>
            <div>
              <Label>Billing Email</Label>
              <Input
                type="email"
                value={profileForm.billingEmail}
                onChange={e => setProfileForm({ ...profileForm, billingEmail: e.target.value })}
                placeholder="billing@example.com"
                data-testid="input-billing-email"
              />
            </div>
            <div>
              <Label>Billing Phone</Label>
              <Input
                value={profileForm.billingPhone}
                onChange={e => setProfileForm({ ...profileForm, billingPhone: e.target.value })}
                placeholder="+1 (555) 000-0000"
                data-testid="input-billing-phone"
              />
            </div>
            <Button
              className="w-full"
              onClick={() => {
                if (!profileCleanerId) return;
                saveProfileMutation.mutate({ cleanerUserId: profileCleanerId, data: profileForm });
              }}
              disabled={!profileForm.companyName || saveProfileMutation.isPending}
              data-testid="button-save-profile"
            >
              {saveProfileMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : "Save Profile"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteInvoiceId !== null} onOpenChange={(open) => { if (!open) setDeleteInvoiceId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this invoice? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteInvoiceId && deleteMutation.mutate(deleteInvoiceId)}
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog for invoices received FROM service orgs (owner_invoice format) */}
      <Dialog open={selectedReceivedInvoice !== null} onOpenChange={(open) => { if (!open) setSelectedReceivedInvoice(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedReceivedInvoice && (() => {
            const inv = selectedReceivedInvoice;
            const from = inv.adminCompanySnapshot;
            const to = inv.companySnapshot;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <span className="font-mono">{inv.invoiceNumber || `INV-${inv.id}`}</span>
                    <Badge variant={statusVariant(inv.status)} className={statusClassName(inv.status)}>
                      {statusLabel(inv.status)}
                    </Badge>
                  </DialogTitle>
                  <DialogDescription>
                    Invoice from {inv.fromOrgName || `Org #${inv.organizationId}`}
                  </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">From</p>
                    <p className="font-semibold">{from?.companyName || inv.fromOrgName || `Org #${inv.organizationId}`}</p>
                    {from?.taxId && <p className="text-muted-foreground">Tax ID: {from.taxId}</p>}
                    {from?.addressLine1 && <p className="text-muted-foreground">{from.addressLine1}</p>}
                    {from?.city && <p className="text-muted-foreground">{from.city}{from.state ? `, ${from.state}` : ''} {from.postalCode || ''}</p>}
                    {from?.billingEmail && <p className="text-muted-foreground">{from.billingEmail}</p>}
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">To</p>
                    <p className="font-semibold">{to?.companyName || "Your Organization"}</p>
                    {to?.taxId && <p className="text-muted-foreground">Tax ID: {to.taxId}</p>}
                    {to?.addressLine1 && <p className="text-muted-foreground">{to.addressLine1}</p>}
                    {to?.city && <p className="text-muted-foreground">{to.city}{to.state ? `, ${to.state}` : ''} {to.postalCode || ''}</p>}
                    {to?.billingEmail && <p className="text-muted-foreground">{to.billingEmail}</p>}
                  </div>
                </div>

                <div className="flex gap-6 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Period</p>
                    <p className="font-medium">
                      {inv.periodStart && inv.periodEnd
                        ? `${formatDate(inv.periodStart)} — ${formatDate(inv.periodEnd)}`
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <p className="font-medium capitalize">{inv.status}</p>
                  </div>
                </div>

                {inv.notes && (
                  <div className="rounded-md bg-muted px-3 py-2 text-sm">
                    <p className="text-xs text-muted-foreground mb-1">Notes</p>
                    <p>{inv.notes}</p>
                  </div>
                )}

                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(inv.lineItems || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-muted-foreground py-6">No line items</TableCell>
                        </TableRow>
                      ) : (inv.lineItems || []).map((item: any, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell>{item.description}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">{item.date ? formatDate(item.date) : "—"}</TableCell>
                          <TableCell className="text-right font-medium">{formatMoney(item.amount)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="font-bold bg-muted/50">
                        <TableCell colSpan={2} className="text-right">Total</TableCell>
                        <TableCell className="text-right">{formatMoney(inv.total)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                {["issued", "sent", "partially_paid"].includes(inv.status) && inv.publicToken && (
                  <div className="rounded-md border border-green-200 bg-green-50 p-4">
                    <p className="text-sm font-medium flex items-center gap-1.5 mb-3 text-green-800">
                      <CreditCard className="h-4 w-4" />
                      Pay this invoice online
                    </p>
                    <a
                      href={`/pay/${inv.publicToken}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-4 py-2 transition-colors"
                    >
                      <Link2 className="h-4 w-4" />
                      Pay online →
                    </a>
                    <p className="text-xs text-muted-foreground mt-2 break-all">
                      {window.location.origin}/pay/{inv.publicToken}
                    </p>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    onClick={() => generateInvoicePdf({
                      ...inv,
                      ownerName: inv.companySnapshot?.companyName || "PM Organization",
                      orgLogoUrl: null,
                      payLinkUrl: inv.publicToken ? `${window.location.origin}/pay/${inv.publicToken}` : undefined,
                    })}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
